import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Badge, Form, Button, Spinner, InputGroup, Dropdown } from 'react-bootstrap';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Course } from '../../types';
import { getAllCourses, getTeachers } from '../../services/courseService';
import { FaStar, FaUser, FaClock, FaSearch, FaFilter, FaSort, FaUsers, FaBook, FaGraduationCap, FaTag, FaLayerGroup } from 'react-icons/fa';
import 'animate.css';

const CoursesPage: React.FC = () => {
  const [courses, setCourses] = useState<Course[]>([]);
  const [filteredCourses, setFilteredCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterTeacher, setFilterTeacher] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [teachers, setTeachers] = useState<{id: string, name: string}[]>([]);
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 10000]);
  const [animateCards, setAnimateCards] = useState(false);
  
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // Extract search query from URL if present
    const queryParams = new URLSearchParams(location.search);
    const searchQuery = queryParams.get('search');
    if (searchQuery) {
      setSearchTerm(searchQuery);
    }
    
    fetchCourses();
    fetchTeachers();
  }, [location.search]);

  const fetchCourses = async () => {
    try {
      setLoading(true);
      setError(null);
      const fetchedCourses = await getAllCourses();
      setCourses(fetchedCourses);
      setFilteredCourses(fetchedCourses);
      // Trigger animation after courses are loaded
      setTimeout(() => {
        setAnimateCards(true);
      }, 300);
    } catch (error) {
      console.error('Error fetching courses:', error);
      setError('حدث خطأ أثناء تحميل الكورسات');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchTeachers = async () => {
    try {
      const teachersList = await getTeachers();
      setTeachers(teachersList);
    } catch (error) {
      console.error('Error fetching teachers:', error);
    }
  };

  useEffect(() => {
    filterAndSortCourses();
  }, [searchTerm, filterCategory, filterTeacher, sortBy, priceRange, courses]);

  const filterAndSortCourses = () => {
    let results = [...courses];
    
    // Apply search filter
    if (searchTerm) {
      results = results.filter(course => 
        (course.title?.toLowerCase().includes(searchTerm.toLowerCase()) || false) || 
        (course.description?.toLowerCase().includes(searchTerm.toLowerCase()) || false) ||
        (course.teacherName?.toLowerCase().includes(searchTerm.toLowerCase()) || false)
      );
    }
    
    // Apply category filter
    if (filterCategory) {
      results = results.filter(course => course.category === filterCategory);
    }
    
    // Apply teacher filter
    if (filterTeacher) {
      results = results.filter(course => course.teacherId === filterTeacher);
    }
    
    // Apply price range filter
    results = results.filter(course => {
      const price = course.price || 0;
      return price >= priceRange[0] && price <= priceRange[1];
    });
    
    // Apply sorting
    switch (sortBy) {
      case 'newest':
        results.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
        break;
      case 'oldest':
        results.sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));
        break;
      case 'price-asc':
        results.sort((a, b) => (a.price || 0) - (b.price || 0));
        break;
      case 'price-desc':
        results.sort((a, b) => (b.price || 0) - (a.price || 0));
        break;
      case 'rating':
        results.sort((a, b) => (b.rating || 0) - (a.rating || 0));
        break;
      case 'students':
        results.sort((a, b) => (b.studentsCount || 0) - (a.studentsCount || 0));
        break;
      default:
        break;
    }
    
    setFilteredCourses(results);
    // Reset animation state when filters change
    setAnimateCards(false);
    setTimeout(() => {
      setAnimateCards(true);
    }, 300);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    updateQueryParams('search', e.target.value);
  };

  const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setFilterCategory(e.target.value);
    updateQueryParams('category', e.target.value);
  };
  
  const handleTeacherChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setFilterTeacher(e.target.value);
    updateQueryParams('teacher', e.target.value);
  };
  
  const handleSortChange = (sortValue: string) => {
    setSortBy(sortValue);
    updateQueryParams('sort', sortValue);
  };
  
  const handlePriceRangeChange = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const newValue = parseInt(e.target.value);
    const newPriceRange: [number, number] = [...priceRange];
    newPriceRange[index] = newValue;
    setPriceRange(newPriceRange);
  };
  
  const updateQueryParams = (param: string, value: string) => {
    const queryParams = new URLSearchParams(location.search);
    if (value) {
      queryParams.set(param, value);
    } else {
      queryParams.delete(param);
    }
    navigate({ search: queryParams.toString() });
  };
  
  const resetFilters = () => {
    setSearchTerm('');
    setFilterCategory('');
    setFilterTeacher('');
    setSortBy('newest');
    setPriceRange([0, 10000]);
    navigate('/courses');
  };

  const categories = Array.from(new Set(courses.map(course => course.category || 'غير مصنف')));

  const renderStars = (rating?: number) => {
    const stars = [];
    const ratingValue = rating || 0;
    for (let i = 0; i < 5; i++) {
      stars.push(
        <FaStar
          key={i}
          className={i < ratingValue ? 'text-warning' : 'text-muted'}
        />
      );
    }
    return stars;
  };

  if (loading && courses.length === 0) {
    return (
      <div className="text-center my-5">
        <div className="spinner-container">
          <Spinner animation="border" className="spinner-primary" />
          <div className="spinner-overlay"></div>
        </div>
        <p className="mt-2">جاري تحميل الكورسات...</p>
      </div>
    );
  }

  return (
    <div className="courses-page py-4">
      <Container>
        <div className="section-header text-center mb-5 animate__animated animate__fadeInDown">
          <h1 className="section-title">
            <FaGraduationCap className="me-2 text-primary" />
            استكشف الكورسات
          </h1>
          <p className="section-subtitle">اكتشف مجموعة متنوعة من الكورسات في مختلف المجالات</p>
        </div>

        {error && (
          <div className="alert alert-danger animate__animated animate__shakeX" role="alert">
            {error}
          </div>
        )}

        <Row className="mb-4 animate__animated animate__fadeIn">
          <Col lg={8}>
            <InputGroup className="mb-3 search-input-group">
              <Form.Control
                type="search"
                placeholder="ابحث عن كورس أو معلم..."
                value={searchTerm}
                onChange={handleSearchChange}
                className="search-input"
              />
              <Button variant="primary" className="search-button">
                <FaSearch /> بحث
              </Button>
            </InputGroup>
          </Col>
          <Col lg={4} className="d-flex justify-content-end align-items-center">
            <span className="me-2">ترتيب حسب:</span>
            <Dropdown>
              <Dropdown.Toggle variant="outline-secondary" id="dropdown-sort" className="sort-dropdown">
                <FaSort className="me-1" />
                {sortBy === 'newest' && 'الأحدث'}
                {sortBy === 'oldest' && 'الأقدم'}
                {sortBy === 'price-asc' && 'السعر: من الأقل للأعلى'}
                {sortBy === 'price-desc' && 'السعر: من الأعلى للأقل'}
                {sortBy === 'rating' && 'التقييم'}
                {sortBy === 'students' && 'عدد الطلاب'}
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item onClick={() => handleSortChange('newest')}>الأحدث</Dropdown.Item>
                <Dropdown.Item onClick={() => handleSortChange('oldest')}>الأقدم</Dropdown.Item>
                <Dropdown.Item onClick={() => handleSortChange('price-asc')}>السعر: من الأقل للأعلى</Dropdown.Item>
                <Dropdown.Item onClick={() => handleSortChange('price-desc')}>السعر: من الأعلى للأقل</Dropdown.Item>
                <Dropdown.Item onClick={() => handleSortChange('rating')}>التقييم</Dropdown.Item>
                <Dropdown.Item onClick={() => handleSortChange('students')}>عدد الطلاب</Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </Col>
        </Row>

        <Row>
          {/* Filters Sidebar */}
          <Col lg={3} className="mb-4">
            <Card className="filter-sidebar animate__animated animate__fadeInRight">
              <Card.Header className="d-flex justify-content-between align-items-center filter-header">
                <h5 className="mb-0">
                  <FaFilter className="me-2 filter-icon" />
                  تصفية النتائج
                </h5>
                <Button variant="link" className="p-0 text-decoration-none reset-button" onClick={resetFilters}>
                  إعادة ضبط
                </Button>
              </Card.Header>
              <Card.Body>
                <Form>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      <FaLayerGroup className="me-1 text-primary" /> التصنيف
                    </Form.Label>
                    <Form.Select 
                      value={filterCategory} 
                      onChange={handleCategoryChange}
                      className="filter-select"
                    >
                      <option value="">جميع التصنيفات</option>
                      {categories.map((category, index) => (
                        <option key={index} value={category}>
                          {category}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>
                      <FaUser className="me-1 text-primary" /> المعلم
                    </Form.Label>
                    <Form.Select 
                      value={filterTeacher} 
                      onChange={handleTeacherChange}
                      className="filter-select"
                    >
                      <option value="">جميع المعلمين</option>
                      {teachers.map((teacher) => (
                        <option key={teacher.id} value={teacher.id}>
                          {teacher.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>
                      <FaTag className="me-1 text-primary" /> نطاق السعر
                    </Form.Label>
                    <div className="d-flex align-items-center mb-2">
                      <Form.Control
                        type="number"
                        min="0"
                        max={priceRange[1]}
                        value={priceRange[0]}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handlePriceRangeChange(e, 0)}
                        className="me-2 price-input"
                      />
                      <span>إلى</span>
                      <Form.Control
                        type="number"
                        min={priceRange[0]}
                        value={priceRange[1]}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handlePriceRangeChange(e, 1)}
                        className="ms-2 price-input"
                      />
                    </div>
                    <div className="price-range-slider">
                      <Form.Range
                        min="0"
                        max="10000"
                        step="100"
                        value={priceRange[0]}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handlePriceRangeChange(e, 0)}
                        className="mb-2 custom-range"
                      />
                      <Form.Range
                        min="0"
                        max="10000"
                        step="100"
                        value={priceRange[1]}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handlePriceRangeChange(e, 1)}
                        className="custom-range"
                      />
                    </div>
                  </Form.Group>
                </Form>
              </Card.Body>
            </Card>

            <Card className="mt-4 stats-card animate__animated animate__fadeInRight animate__delay-1s">
              <Card.Body>
                <h5 className="mb-3">
                  <FaBook className="me-2 text-primary" />
                  احصائيات الكورسات
                </h5>
                <div className="course-stats">
                  <div className="stat-item d-flex justify-content-between mb-2">
                    <span>
                      <FaBook className="me-1" /> إجمالي الكورسات:
                    </span>
                    <Badge bg="primary" className="stat-badge">{courses.length}</Badge>
                  </div>
                  <div className="stat-item d-flex justify-content-between mb-2">
                    <span>
                      <FaUsers className="me-1" /> إجمالي المعلمين:
                    </span>
                    <Badge bg="info" className="stat-badge">{teachers.length}</Badge>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>

          {/* Courses Grid */}
          <Col lg={9}>
            {loading && (
              <div className="text-center my-3 animate__animated animate__pulse animate__infinite">
                <Spinner animation="border" size="sm" />
                <span className="ms-2">جاري تحديث النتائج...</span>
              </div>
            )}
            
            {filteredCourses.length === 0 && !loading ? (
              <div className="text-center my-5 py-5 bg-light rounded animate__animated animate__fadeIn">
                <FaSearch size={40} className="text-muted mb-3" />
                <h4>لا توجد كورسات متطابقة مع البحث</h4>
                <p className="text-muted">حاول تغيير معايير البحث أو التصفية</p>
                <Button variant="primary" onClick={resetFilters} className="pulse-button">
                  عرض جميع الكورسات
                </Button>
              </div>
            ) : (
              <Row xs={1} md={2} xl={3} className="g-4">
                {filteredCourses.map((course, index) => (
                  <Col key={course.id}>
                    <Card 
                      className={`h-100 course-card ${animateCards ? 'animate__animated animate__fadeInUp' : ''}`} 
                      style={{ animationDelay: `${index * 0.1}s` }}
                    >
                      <div className="course-image-container">
                        {course.imageUrl ? (
                          <Card.Img
                            variant="top"
                            src={course.imageUrl}
                            alt={course.title}
                            className="course-image"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.onerror = null;
                              target.src = 'https://via.placeholder.com/300x200?text=كورس';
                            }}
                          />
                        ) : (
                          <Card.Img
                            variant="top"
                            src="https://via.placeholder.com/300x200?text=كورس"
                            alt={course.title}
                            className="course-image"
                          />
                        )}
                        {course.category && (
                          <Badge bg="primary" className="category-badge">
                            {course.category}
                          </Badge>
                        )}
                      </div>
                      <Card.Body>
                        <Card.Title className="course-title">{course.title}</Card.Title>
                        <div className="instructor-info mb-2">
                          <img 
                            src={course.teacherImage || 'https://via.placeholder.com/30x30?text=المعلم'} 
                            alt={course.teacherName || 'غير معروف'}
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.onerror = null;
                              target.src = 'https://via.placeholder.com/30x30?text=المعلم';
                            }}
                          />
                          <span>{course.teacherName || 'غير معروف'}</span>
                        </div>
                        <Card.Text className="course-description">
                          {course.description && course.description.length > 100
                            ? `${course.description.substring(0, 100)}...`
                            : course.description}
                        </Card.Text>
                        <div className="course-meta">
                          <div className="rating mb-2">
                            {renderStars(course.rating)}
                            <span className="ms-1">({course.rating || 0})</span>
                          </div>
                          <div className="d-flex justify-content-between align-items-center">
                            <div className="students-count">
                              <FaUsers className="me-1 text-muted" />
                              <small>{course.studentsCount || 0} طالب</small>
                            </div>
                            <div className="duration">
                              <FaClock className="me-1 text-muted" />
                              <small>{course.duration || '3 ساعات'}</small>
                            </div>
                          </div>
                        </div>
                      </Card.Body>
                      <Card.Footer className="d-flex justify-content-between align-items-center">
                        <div className="price">
                          {course.price ? (
                            <span className="fw-bold text-primary">{course.price} ج.م</span>
                          ) : (
                            <span className="fw-bold text-success">مجاني</span>
                          )}
                        </div>
                        <Link to={`/courses/${course.id}`} className="btn btn-sm btn-outline-primary view-course-btn">
                          عرض الكورس
                        </Link>
                      </Card.Footer>
                      <div className="course-card-overlay"></div>
                    </Card>
                  </Col>
                ))}
              </Row>
            )}
          </Col>
        </Row>
      </Container>
      
      <style>{`
        .section-title {
          color: #0d6efd;
          font-weight: bold;
          margin-bottom: 1rem;
        }
        
        .section-subtitle {
          color: #6c757d;
          margin-bottom: 2rem;
        }
        
        .course-card {
          transition: transform 0.3s ease, box-shadow 0.3s ease;
          border: none;
          border-radius: 10px;
          overflow: hidden;
          position: relative;
          box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
        }
        
        .course-card:hover {
          transform: translateY(-10px);
          box-shadow: 0 15px 30px rgba(0, 0, 0, 0.1);
        }
        
        .course-card:hover .course-image {
          transform: scale(1.05);
        }
        
        .course-image-container {
          position: relative;
          overflow: hidden;
          height: 160px;
        }
        
        .course-image {
          transition: transform 0.5s ease;
          height: 100%;
          object-fit: cover;
        }
        
        .category-badge {
          position: absolute;
          top: 10px;
          right: 10px;
          z-index: 1;
          transition: all 0.3s ease;
        }
        
        .course-card:hover .category-badge {
          transform: translateY(-3px);
        }
        
        .course-title {
          font-size: 1.1rem;
          font-weight: 600;
          margin-bottom: 0.5rem;
          color: #343a40;
          height: 50px;
          overflow: hidden;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
        }
        
        .instructor-info {
          font-size: 0.9rem;
          color: #6c757d;
        }
        
        .course-description {
          font-size: 0.85rem;
          color: #6c757d;
          margin-bottom: 1rem;
          height: 60px;
          overflow: hidden;
        }
        
        .course-meta {
          font-size: 0.85rem;
        }
        
        .rating {
          display: flex;
          align-items: center;
        }
        
        .filter-sidebar {
          border-radius: 10px;
          overflow: hidden;
          box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
          border: none;
        }
        
        .filter-header {
          background: linear-gradient(135deg, #0d6efd 0%, #0a58ca 100%);
          color: white;
        }
        
        .reset-button {
          color: white;
          font-weight: 500;
          transition: all 0.3s ease;
        }
        
        .reset-button:hover {
          transform: scale(1.05);
          color: white;
        }
        
        .filter-icon {
          animation: pulse 2s infinite;
        }
        
        .filter-select, .price-input {
          border-radius: 5px;
          border: 1px solid #dee2e6;
          transition: all 0.3s ease;
        }
        
        .filter-select:focus, .price-input:focus {
          border-color: #0d6efd;
          box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
        }
        
        .custom-range {
          height: 6px;
        }
        
        .custom-range::-webkit-slider-thumb {
          background: #0d6efd;
        }
        
        .stats-card {
          border-radius: 10px;
          overflow: hidden;
          box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
          border: none;
          background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
        }
        
        .stat-item {
          padding: 8px 0;
          border-bottom: 1px dashed #dee2e6;
        }
        
        .stat-item:last-child {
          border-bottom: none;
        }
        
        .stat-badge {
          transition: all 0.3s ease;
        }
        
        .stat-item:hover .stat-badge {
          transform: scale(1.1);
        }
        
        .search-input-group {
          box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
          border-radius: 5px;
          overflow: hidden;
        }
        
        .search-input {
          border: none;
          padding: 12px 15px;
        }
        
        .search-input:focus {
          box-shadow: none;
        }
        
        .search-button {
          padding-left: 20px;
          padding-right: 20px;
        }
        
        .sort-dropdown {
          border-radius: 5px;
          transition: all 0.3s ease;
        }
        
        .sort-dropdown:hover {
          background-color: #f8f9fa;
          border-color: #0d6efd;
        }
        
        .view-course-btn {
          transition: all 0.3s ease;
          border-radius: 20px;
          padding: 4px 12px;
        }
        
        .view-course-btn:hover {
          background-color: #0d6efd;
          color: white;
          transform: translateX(-5px);
        }
        
        .course-card-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(to bottom, rgba(255,255,255,0) 70%, rgba(255,255,255,0.8) 100%);
          opacity: 0;
          transition: opacity 0.3s ease;
          pointer-events: none;
        }
        
        .course-card:hover .course-card-overlay {
          opacity: 1;
        }
        
        .pulse-button {
          animation: pulse 2s infinite;
        }
        
        .spinner-container {
          position: relative;
          display: inline-block;
        }
        
        .spinner-primary {
          color: #0d6efd !important;
          width: 3rem;
          height: 3rem;
        }
        
        .spinner-overlay {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: radial-gradient(circle, rgba(13,110,253,0.2) 0%, rgba(255,255,255,0) 70%);
          animation: pulse 2s infinite;
          border-radius: 50%;
        }
        
        @keyframes pulse {
          0% {
            transform: scale(1);
            opacity: 1;
          }
          50% {
            transform: scale(1.1);
            opacity: 0.8;
          }
          100% {
            transform: scale(1);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
};

export default CoursesPage;
